import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import drinkingBoyVideo from "./drinking-boy.mp4";

const REMINDER_INTERVAL = 1 * 60 * 60 * 1000; // 1 hour
const REMINDER_DURATION = 10000; // Keep visible for 10 seconds.

const reminderMessages = [
  {
    title: "Hey! Have you had some water? 💧",
    description: "Take a quick sip and keep yourself refreshed.",
  },
  {
    title: "Time for a hydration break! 🌿",
    description: "A little water break can make a big difference.",
  },
  {
    title: "Your next task can wait 5 seconds. 😊",
    description: "Take a sip of water and look after yourself.",
  },
  {
    title: "Stay fresh, stay hydrated! 💦",
    description: "Remember to give your body the water it needs.",
  },
  {
    title: "A little reminder to take care of yourself ❤️",
    description: "Pause for a moment and enjoy a glass of water.",
  },
];

const DrinkingBoyVideo = () => {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: 205 },
        maxWidth: { xs: 300, sm: 205 },
        height: { xs: 170, sm: 190 },
        flex: "0 0 auto",
        borderRadius: 3,
        overflow: "hidden",
        backgroundColor: "#DDF6FC",
        border: "1px solid rgba(67, 183, 211, 0.22)",
        boxShadow: "0 6px 18px rgba(44, 145, 170, 0.10)",
        "& video": {
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center center",
        },
      }}
      aria-label="Cartoon boy drinking water"
      role="img"
    >
      <video
        src={drinkingBoyVideo}
        autoPlay={!prefersReducedMotion}
        muted
        playsInline
        preload="auto"
        disablePictureInPicture
        aria-hidden="true"
      />
    </Box>
  );
};

const HydrationReminder = () => {
  const [open, setOpen] = React.useState(false);
  const [messageIndex, setMessageIndex] = React.useState(-1);

  React.useEffect(() => {
    let hourlyIntervalId;
    let dismissTimeoutId;

    const showReminder = () => {
      setMessageIndex((currentIndex) =>
        currentIndex < 0
          ? 0
          : (currentIndex + 1) % reminderMessages.length
      );
      setOpen(true);

      if (dismissTimeoutId) {
        window.clearTimeout(dismissTimeoutId);
      }

      dismissTimeoutId = window.setTimeout(() => {
        setOpen(false);
      }, REMINDER_DURATION);
    };

    // Wait a full hour after FullLayout mounts (the authenticated dashboard session).
    const firstReminderTimeoutId = window.setTimeout(() => {
      showReminder();
      hourlyIntervalId = window.setInterval(showReminder, REMINDER_INTERVAL);
    }, REMINDER_INTERVAL);

    return () => {
      window.clearTimeout(firstReminderTimeoutId);
      if (hourlyIntervalId) {
        window.clearInterval(hourlyIntervalId);
      }
      if (dismissTimeoutId) {
        window.clearTimeout(dismissTimeoutId);
      }
    };
  }, []);

  const currentMessage = reminderMessages[Math.max(messageIndex, 0)];

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: "rgba(226, 244, 249, 0.16)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        pointerEvents: "none",
      }}
    >
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 760,
          boxSizing: "border-box",
          overflow: "hidden",
          transform: "translateY(-40px)",
          display: "flex",
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 3 },
          p: { xs: 2, sm: 2.5 },
          pt: { xs: 4.5, sm: 3.5 },
          borderRadius: 4,
          border: "1px solid #C7EAF3",
          borderTop: "4px solid #55C1DC",
          background:
            "linear-gradient(135deg, #F5FCFE 0%, #E8F8FC 54%, #F2FBFD 100%)",
          boxShadow: "0 18px 48px rgba(38, 105, 128, 0.20)",
          color: "#294653",
          pointerEvents: "auto",
          animation: "hydrationPopupEnter 320ms cubic-bezier(.2,.8,.2,1) both",
          "@keyframes hydrationPopupEnter": {
            from: {
              opacity: 0,
              transform: "translateY(-32px) scale(0.97)",
            },
            to: {
              opacity: 1,
              transform: "translateY(-40px) scale(1)",
            },
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        {/* A 10-second progress bar shows how long the reminder stays visible. */}
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            overflow: "hidden",
            backgroundColor: "rgba(85, 193, 220, 0.16)",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              transformOrigin: "left center",
              background: "linear-gradient(90deg, #FF9A80, #55C1DC)",
              animation: `hydrationProgress ${REMINDER_DURATION}ms linear forwards`,
              "@keyframes hydrationProgress": {
                from: { transform: "scaleX(1)" },
                to: { transform: "scaleX(0)" },
              },
            }}
          />
        </Box>

        <IconButton
          size="small"
          aria-label="Dismiss hydration reminder"
          onClick={() => setOpen(false)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 30,
            height: 30,
            color: "#6E8994",
            backgroundColor: "rgba(255,255,255,0.68)",
            border: "1px solid rgba(90,160,180,0.14)",
            "&:hover": {
              backgroundColor: "#FFFFFF",
              color: "#F08068",
            },
            "& span": {
              fontSize: 20,
              lineHeight: 1,
              mt: "-2px",
            },
          }}
        >
          <Box component="span">×</Box>
        </IconButton>

        <DrinkingBoyVideo />

        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.7,
              mb: 1.1,
              px: 1.2,
              py: 0.55,
              borderRadius: 99,
              backgroundColor: "rgba(255,255,255,0.82)",
              color: "#2999B6",
              border: "1px solid rgba(41,153,182,0.12)",
            }}
          >
            <Box component="span" sx={{ fontSize: 13 }} aria-hidden="true">
              💧
            </Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0.85,
                lineHeight: 1.2,
              }}
            >
              A QUICK WELLNESS BREAK
            </Typography>
          </Box>

          <Typography
            sx={{
              color: "#F27F68",
              fontWeight: 800,
              fontSize: { xs: 20, sm: 24 },
              lineHeight: 1.3,
              letterSpacing: "-0.35px",
            }}
          >
            {currentMessage.title}
          </Typography>

          <Typography
            sx={{
              color: "#5B7784",
              fontSize: { xs: 14, sm: 15 },
              lineHeight: 1.6,
              mt: 0.75,
            }}
          >
            {currentMessage.description}
          </Typography>

          <Typography
            sx={{
              color: "#2999B6",
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 0.65,
              mt: 1.5,
            }}
          >
            TAKE A SIP · TAKE A BREATH · FEEL REFRESHED
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HydrationReminder;
